import { useResourceUploadActivity } from "~/components/ResourceUploadField/UploadScope"
import { useEffect, useMemo, useState } from "react"
import {
  Button,
  Checkbox,
  Empty,
  Flex,
  message,
  Modal,
  Pagination,
  Segmented,
  Spin,
  Typography,
} from "antd"
import classNames from "classnames"
import { InboxOutlined } from "@ant-design/icons"
import { ResourcePreview } from "~/components/ResourcePreview"
import { Uploader } from "~/components/Uploader"
import { useVideoCompressionProgress } from "~/common/hooks"
import {
  getUploadableLocalFiles,
  uploadLocalResources,
  validateLocalUploadVideoDurations,
  type LocalUploadFile,
} from "~/common/media/resourceUpload"
import { RESOURCE_UPLOAD_PASTE_PRIORITY } from "~/common/media/resourceUploadHooks"
import {
  getUploadedResourceKind,
  IMAGE_UPLOAD_REQUIREMENTS,
  uploadResourceLimits,
  VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES,
  type UploadedResource,
  type UploadedResourceKind,
} from "~/common/media/uploadedResources"
import { getResourceFromMaterial, listMaterials } from "~/api/material"
import { notifyUploadFailures } from "~/common/media/uploadFeedback"
import styles from "./index.css"

const { Text } = Typography

const resourcePageSize = 24
const emptySelectedResources: UploadedResource[] = []
const emptySelectedUrls: string[] = []
const mixedImageAudioUploadSuccessMessage =
  "图片与音频已上传成功，你可在左上方切换【图片】/【音频】标签查看、勾选素材"
const getUploadSuccessMessage = (count: number) => {
  return count > 1 ? `已上传 ${count} 个素材` : "上传成功"
}

type ResourceKindFilter = "all" | "audio" | "image" | "video"

type ResourceLibraryModalProps = {
  open: boolean
  mode?: "manage" | "select"
  onCancel: () => void
  title?: string
  resourceType?: ResourceKindFilter | ResourceKindFilter[]
  selectionMode?: "single" | "multiple"
  selectedResources?: UploadedResource[]
  selectedUrls?: string[]
  confirmText?: string
  maxCount?: number
  videoTargetSizeMegabytes?: number
  maxCountByKind?: Partial<Record<SelectableResourceKind, number>>
  onConfirm?: (resources: UploadedResource[]) => void
}

type SelectableResourceKind = Exclude<ResourceKindFilter, "all">

const allResourceKinds: SelectableResourceKind[] = ["image", "video", "audio"]

const resourceKindLabels: Record<ResourceKindFilter, string> = {
  all: "全部",
  image: "图片",
  video: "视频",
  audio: "音频",
}

const getUploadHints = (
  videoTargetSizeMegabytes: number,
): Record<SelectableResourceKind, string> => ({
  image: IMAGE_UPLOAD_REQUIREMENTS,
  video: `视频 MP4 原文件超过 ${videoTargetSizeMegabytes}MB 会在上传前自动压缩`,
  audio: "音频 MP3/MPEG/WAV 不超过 10MB",
})

const getAllowedResourceKinds = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
) => {
  const resourceTypes = Array.isArray(resourceType)
    ? resourceType
    : [resourceType]

  if (resourceTypes.includes("all")) return allResourceKinds

  const allowedKinds = allResourceKinds.filter((kind) =>
    resourceTypes.includes(kind),
  )

  return allowedKinds.length > 0 ? allowedKinds : allResourceKinds
}

const isAllResourceType = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
) => {
  const resourceTypes = Array.isArray(resourceType)
    ? resourceType
    : [resourceType]

  return resourceTypes.includes("all")
}

const getDefaultResourceKindFilter = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
): ResourceKindFilter => {
  if (isAllResourceType(resourceType)) return "all"

  return getAllowedResourceKinds(resourceType)[0]
}

const getResourceKindFilterOptions = (
  allowedKinds: SelectableResourceKind[],
  showAllOption: boolean,
): Array<{
  label: string
  value: ResourceKindFilter
}> => {
  return [
    ...(showAllOption
      ? [{ label: resourceKindLabels.all, value: "all" as const }]
      : []),
    ...allowedKinds.map((kind) => ({
      label: resourceKindLabels[kind],
      value: kind,
    })),
  ]
}

const getAccept = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
) => {
  return getAllowedResourceKinds(resourceType)
    .map((kind) => uploadResourceLimits[kind].accept)
    .filter(Boolean)
    .join(",")
}

const getUploadHint = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
  videoTargetSizeMegabytes: number,
) => {
  const allowedKinds = getAllowedResourceKinds(resourceType)
  const uploadHints = getUploadHints(videoTargetSizeMegabytes)

  return allowedKinds.map((kind) => uploadHints[kind]).join("；")
}

const getUploadFormatError = (
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
) => {
  const limits = getAllowedResourceKinds(resourceType).map(
    (kind) => uploadResourceLimits[kind],
  )
  const extensions = limits.flatMap((limit) => limit.extensions).join(" / ")

  return limits.length === 1
    ? `${limits[0].label}格式仅支持 ${extensions}`
    : `文件格式仅支持 ${extensions}`
}

const isResourceMatched = (
  resource: UploadedResource,
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>,
) => {
  const resourceKind = getUploadedResourceKind(resource)

  if (resourceKind === "file") return false

  return getAllowedResourceKinds(resourceType).includes(resourceKind)
}

const getResourceTypeText = (kind: UploadedResourceKind) => {
  switch (kind) {
    case "image":
      return "图片"
    case "video":
      return "视频"
    case "audio":
      return "音频"
    default:
      return "文件"
  }
}

const getMaterialTypeFilter = ({
  isSelectMode,
  resourceKindFilter,
  resourceType,
}: {
  isSelectMode: boolean
  resourceKindFilter: ResourceKindFilter
  resourceType: NonNullable<ResourceLibraryModalProps["resourceType"]>
}): API.Material.UserMaterialType | undefined => {
  const allowedKinds = getAllowedResourceKinds(resourceType)

  if (
    resourceKindFilter !== "all" &&
    allowedKinds.includes(resourceKindFilter)
  ) {
    return resourceKindFilter
  }

  if (isSelectMode && allowedKinds.length === 1) {
    return allowedKinds[0]
  }

  return undefined
}

const mergeResourceItems = (resources: UploadedResource[]) => {
  const resourceMap = new Map<string, UploadedResource>()

  resources.forEach((resource) => {
    resourceMap.set(resource.url, resource)
  })

  return Array.from(resourceMap.values())
}

export const ResourceLibraryModal = ({
  open,
  mode = "select",
  onCancel,
  title,
  resourceType = "all",
  selectionMode = "multiple",
  selectedResources: initialSelectedResources = emptySelectedResources,
  selectedUrls = emptySelectedUrls,
  confirmText = "确认选择",
  maxCount,
  videoTargetSizeMegabytes = VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES,
  maxCountByKind,
  onConfirm,
}: ResourceLibraryModalProps) => {
  const [resources, setResources] = useState<UploadedResource[]>([])
  const [resourcePage, setResourcePage] = useState(1)
  const [resourceTotal, setResourceTotal] = useState(0)
  const [pendingSelectedUrls, setPendingSelectedUrls] = useState<string[]>([])
  const [cachedSelectedResources, setCachedSelectedResources] = useState<
    UploadedResource[]
  >([])
  const [uploadingCount, setUploadingCount] = useState(0)
  const [isLoadingResources, setIsLoadingResources] = useState(false)
  const [resourceKindFilter, setResourceKindFilter] =
    useState<ResourceKindFilter>(() =>
      getDefaultResourceKindFilter(resourceType),
    )
  const { averageProgress, updateProgress } = useVideoCompressionProgress()
  const isSelectMode = mode === "select"
  const isUploading = uploadingCount > 0
  useResourceUploadActivity(open && isUploading)
  const status =
    averageProgress !== undefined
      ? `压缩视频中 ${Math.round(averageProgress * 100)}%`
      : uploadingCount > 1
        ? `上传中...（${uploadingCount} 个）`
        : "上传中..."
  const resourceTypeKey = Array.isArray(resourceType)
    ? resourceType.join("|")
    : resourceType
  const allowedResourceKinds = useMemo(
    () => getAllowedResourceKinds(resourceType),
    [resourceTypeKey],
  )
  const selectionLimit =
    maxCount ??
    (maxCountByKind &&
    allowedResourceKinds.every((kind) => maxCountByKind[kind] !== undefined)
      ? allowedResourceKinds.reduce(
          (total, kind) => total + (maxCountByKind[kind] ?? 0),
          0,
        )
      : undefined)
  const allowedResourceKindKey = allowedResourceKinds.join("|")
  const showAllResourceKindFilter = useMemo(
    () => isAllResourceType(resourceType),
    [resourceTypeKey],
  )
  const defaultResourceKindFilter = useMemo(
    () => getDefaultResourceKindFilter(resourceType),
    [resourceTypeKey],
  )
  const effectiveResourceKindFilter =
    (resourceKindFilter === "all" && showAllResourceKindFilter) ||
    (resourceKindFilter !== "all" &&
      allowedResourceKinds.includes(resourceKindFilter))
      ? resourceKindFilter
      : defaultResourceKindFilter
  const currentResourceKindFilterOptions = useMemo(
    () =>
      getResourceKindFilterOptions(
        allowedResourceKinds,
        showAllResourceKindFilter,
      ),
    [allowedResourceKindKey, allowedResourceKinds, showAllResourceKindFilter],
  )
  const showResourceKindFilter =
    !isSelectMode || allowedResourceKinds.length > 1
  const selectedInputUrls = initialSelectedResources.length
    ? initialSelectedResources.map((resource) => resource.url)
    : selectedUrls
  const selectedUrlKey = selectedInputUrls.join("\n")
  const modalTitle = title || (isSelectMode ? "上传/选择资源" : "我的资源")
  const resourceKindMaterialType = getMaterialTypeFilter({
    isSelectMode,
    resourceKindFilter: effectiveResourceKindFilter,
    resourceType,
  })
  const selectableResources = useMemo(() => {
    return resources.filter((resource) =>
      isResourceMatched(resource, resourceType),
    )
  }, [resourceType, resources])
  const filteredResources = useMemo(() => {
    return selectableResources.filter((resource) => {
      const resourceKind = getUploadedResourceKind(resource)

      return (
        effectiveResourceKindFilter === "all" ||
        resourceKind === effectiveResourceKindFilter
      )
    })
  }, [effectiveResourceKindFilter, selectableResources])
  const selectedResourceItems = useMemo(() => {
    const resourceMap = new Map<string, UploadedResource>()

    initialSelectedResources.forEach((resource) => {
      resourceMap.set(resource.url, resource)
    })
    cachedSelectedResources.forEach((resource) => {
      resourceMap.set(resource.url, resource)
    })
    selectableResources.forEach((resource) => {
      resourceMap.set(resource.url, resource)
    })

    return pendingSelectedUrls.map((url) => {
      return resourceMap.get(url) || { url, uploadedAt: Date.now() }
    })
  }, [
    cachedSelectedResources,
    initialSelectedResources,
    pendingSelectedUrls,
    selectableResources,
  ])
  const selectionSummary = allowedResourceKinds
    .flatMap((kind) => {
      const limit = maxCountByKind?.[kind]
      if (limit === undefined) return []

      const count = selectedResourceItems.filter(
        (resource) => getUploadedResourceKind(resource) === kind,
      ).length
      return `${resourceKindLabels[kind]} ${count}/${limit} ${kind === "image" ? "张" : "个"}`
    })
    .join(" · ")
  const refreshResources = async (page = resourcePage) => {
    setIsLoadingResources(true)

    try {
      const response = await listMaterials({
        material_type: resourceKindMaterialType,
        page: page - 1,
        page_size: resourcePageSize,
      })
      const remoteResources =
        response.data?.items.map(getResourceFromMaterial) || []
      const nextTotal = response.data?.total || 0
      const maxPage = Math.max(1, Math.ceil(nextTotal / resourcePageSize))

      setResources(remoteResources)
      setResourceTotal(nextTotal)

      if (page > maxPage) {
        setResourcePage(maxPage)
      }
    } catch {
      setResources([])
      setResourceTotal(0)
    } finally {
      setIsLoadingResources(false)
    }
  }

  const showMaxCountWarning = () => {
    if (selectionLimit === undefined) return

    void message.warning(`最多只能选择 ${selectionLimit} 个资源`)
  }

  const cacheSelectedResource = (resource: UploadedResource) => {
    setCachedSelectedResources((currentValue) => {
      return mergeResourceItems([...currentValue, resource])
    })
  }

  const selectResource = (resource: UploadedResource) => {
    if (!isSelectMode) return

    cacheSelectedResource(resource)

    setPendingSelectedUrls((currentValue) => {
      const url = resource.url

      if (selectionMode === "single") {
        return currentValue[0] === url ? [] : [url]
      }

      if (currentValue.includes(url)) {
        return currentValue.filter((item) => item !== url)
      }

      if (
        selectionLimit !== undefined &&
        currentValue.length >= selectionLimit
      ) {
        showMaxCountWarning()
        return currentValue
      }

      return [...currentValue, url]
    })
  }

  const selectUploadedResources = (uploadedResources: UploadedResource[]) => {
    if (!isSelectMode) return

    const matchedResources = uploadedResources.filter((resource) =>
      isResourceMatched(resource, resourceType),
    )

    if (!matchedResources.length) return

    setCachedSelectedResources((currentValue) => {
      return mergeResourceItems([...currentValue, ...matchedResources])
    })
    setPendingSelectedUrls((currentValue) => {
      if (selectionMode === "single") {
        return [matchedResources[matchedResources.length - 1].url]
      }

      const nextUrls = [...currentValue]

      matchedResources.forEach((resource) => {
        if (nextUrls.includes(resource.url)) return

        if (selectionLimit !== undefined && nextUrls.length >= selectionLimit) {
          showMaxCountWarning()
          return
        }

        nextUrls.push(resource.url)
      })

      return nextUrls
    })
  }

  const handleLocalUploadBatchComplete = (
    uploadedResources: UploadedResource[],
  ) => {
    if (!uploadedResources.length) return

    const matchedResources = uploadedResources.filter((resource) =>
      isResourceMatched(resource, resourceType),
    )

    if (!matchedResources.length) return

    const uploadSuccessMessage = getUploadSuccessMessage(
      matchedResources.length,
    )
    const uploadedKinds = new Set(
      matchedResources.map((resource) => getUploadedResourceKind(resource)),
    )
    const onlyUploadedKind =
      uploadedKinds.size === 1 ? Array.from(uploadedKinds)[0] : null
    const isMixedImageAudioUpload =
      uploadedKinds.size === 2 &&
      uploadedKinds.has("image") &&
      uploadedKinds.has("audio")

    if (isMixedImageAudioUpload) {
      void message.success(mixedImageAudioUploadSuccessMessage)
      return
    }

    void message.success(uploadSuccessMessage)
    selectUploadedResources(matchedResources)

    if (
      onlyUploadedKind &&
      (onlyUploadedKind === "image" || onlyUploadedKind === "audio") &&
      allowedResourceKinds.includes(onlyUploadedKind)
    ) {
      setResourceKindFilter(onlyUploadedKind)
      setResourcePage(1)
    }
  }

  const uploadFiles = async (files: LocalUploadFile[]) => {
    setUploadingCount(files.length)

    try {
      const { acceptedFiles: validFiles, validationErrors } =
        await validateLocalUploadVideoDurations(files)

      validationErrors.forEach((validationError) => {
        void message.error(validationError)
      })

      if (!validFiles.length) return

      const { failures, resources: uploadedResources } =
        await uploadLocalResources({
          files: validFiles,
          onVideoCompressionProgress: updateProgress,
          videoTargetSizeMegabytes,
        })

      if (uploadedResources.length) {
        void refreshResources(1)
        setResourcePage(1)
        handleLocalUploadBatchComplete(uploadedResources)
      }

      notifyUploadFailures(failures)
    } finally {
      setUploadingCount(0)
    }
  }

  const queueFiles = (files: File[]) => {
    if (!files.length) return false

    const { acceptedFiles, rejectedFiles, validationErrors } =
      getUploadableLocalFiles({
        allowedKinds: allowedResourceKinds,
        files,
      })

    if (rejectedFiles.length) {
      void message.error(getUploadFormatError(resourceType))
    }

    validationErrors.forEach((validationError) => {
      void message.error(validationError)
    })

    if (!acceptedFiles.length) return false

    void uploadFiles(acceptedFiles)
    return true
  }

  const handleConfirm = () => {
    if (isUploading) return
    if (!isSelectMode) {
      onCancel()
      return
    }

    onConfirm?.(selectedResourceItems)
  }

  useEffect(() => {
    if (!open) return

    void refreshResources()
  }, [open, resourceKindMaterialType, resourcePage])

  useEffect(() => {
    if (!open) return

    setResourcePage(1)
  }, [open, resourceKindMaterialType])

  useEffect(() => {
    if (resourceKindFilter === effectiveResourceKindFilter) return

    setResourceKindFilter(effectiveResourceKindFilter)
  }, [effectiveResourceKindFilter, resourceKindFilter])

  useEffect(() => {
    if (!open || !isSelectMode) return

    setPendingSelectedUrls(selectedUrlKey.split("\n").filter(Boolean))
    setCachedSelectedResources((currentValue) => {
      return mergeResourceItems([...currentValue, ...initialSelectedResources])
    })
  }, [initialSelectedResources, isSelectMode, open, selectedUrlKey])

  useEffect(() => {
    if (open || !isSelectMode) return

    setPendingSelectedUrls([])
  }, [isSelectMode, open])

  return (
    <Modal
      width={1000}
      footer={
        isSelectMode ? (
          <Flex
            align="center"
            justify="space-between"
          >
            <Text type="secondary">
              已选择{" "}
              {selectionSummary ||
                `${pendingSelectedUrls.length}${selectionLimit !== undefined ? `/${selectionLimit}` : ""} 个资源`}
            </Text>
            <Flex gap={8}>
              <Button onClick={onCancel}>取消</Button>
              <Button
                disabled={isUploading || pendingSelectedUrls.length === 0}
                onClick={handleConfirm}
                type="primary"
              >
                {confirmText}
              </Button>
            </Flex>
          </Flex>
        ) : null
      }
      open={open}
      title={modalTitle}
      onCancel={onCancel}
    >
      <Flex
        className={styles.body}
        gap={20}
      >
        <Flex
          className={styles.uploadPanel}
          vertical
        >
          {open ? (
            <Uploader
              accept={getAccept(resourceType)}
              allowRemoteImage={allowedResourceKinds.includes("image")}
              className={styles.uploadArea}
              description={
                isUploading
                  ? `${status}，完成后会自动保存到我的资源`
                  : getUploadHint(resourceType, videoTargetSizeMegabytes)
              }
              icon={<InboxOutlined className={styles.uploadIcon} />}
              loading={isUploading}
              multiple={selectionMode === "multiple"}
              pasteActivation="always"
              pastePriority={RESOURCE_UPLOAD_PASTE_PRIORITY.MODAL}
              title="本地上传"
              onFiles={queueFiles}
            />
          ) : null}
          <Text type="secondary">上传成功后会自动保存到我的资源。</Text>
        </Flex>

        <Flex
          className={styles.libraryPanel}
          vertical
        >
          <Flex
            align="center"
            justify="space-between"
            wrap
            gap={12}
          >
            {showResourceKindFilter ? (
              <Segmented
                className={styles.resourceKindFilter}
                options={currentResourceKindFilterOptions}
                value={effectiveResourceKindFilter}
                onChange={(value) =>
                  setResourceKindFilter(value as ResourceKindFilter)
                }
              />
            ) : (
              <Text strong>我的资源</Text>
            )}
            <Text type="secondary">共 {resourceTotal} 个资源</Text>
          </Flex>

          {isLoadingResources && filteredResources.length === 0 ? (
            <Flex
              className={styles.emptyState}
              align="center"
              justify="center"
            >
              <Spin description="加载中" />
            </Flex>
          ) : filteredResources.length === 0 ? (
            <Flex
              className={styles.emptyState}
              align="center"
              justify="center"
            >
              <Empty description="暂无资源" />
            </Flex>
          ) : (
            <Flex
              className={styles.resourceList}
              vertical
              gap={14}
            >
              <Flex
                className={styles.grid}
                gap={12}
                wrap
              >
                {filteredResources.map((resource) => {
                  const checked = pendingSelectedUrls.includes(resource.url)
                  const resourceKind = getUploadedResourceKind(resource)

                  return (
                    <Flex
                      key={resource.id || resource.url}
                      className={classNames(
                        styles.item,
                        checked && styles.itemSelected,
                      )}
                    >
                      {isSelectMode ? (
                        <Checkbox
                          checked={checked}
                          className={styles.selector}
                          onChange={() => selectResource(resource)}
                        />
                      ) : null}
                      {resourceKind === "image" ? (
                        <ResourcePreview
                          type="image"
                          src={resource.url}
                          alt={resource.name || "已上传图片"}
                        />
                      ) : null}
                      {resourceKind === "video" ? (
                        <ResourcePreview
                          type="video"
                          src={resource.url}
                        />
                      ) : null}
                      {resourceKind === "audio" ? (
                        <ResourcePreview
                          type="audio"
                          fingerprint={resource.id || resource.url}
                          label={resource.name || "音频素材"}
                          src={resource.url}
                        />
                      ) : null}
                      {resourceKind === "file" ? (
                        <ResourcePreview
                          type="file"
                          label={getResourceTypeText(resourceKind)}
                        />
                      ) : null}
                    </Flex>
                  )
                })}
              </Flex>
              <Flex justify="flex-end">
                <Pagination
                  current={resourcePage}
                  pageSize={resourcePageSize}
                  showSizeChanger={false}
                  size="small"
                  total={resourceTotal}
                  onChange={setResourcePage}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Modal>
  )
}
